import { assertNotEmpty } from '@festina-life/core'
import { getFPlusLanguageServer } from '@festina-life/fplus'
import { useDebounceCallback } from '@festina-life/ui/hooks'
import { type ReactNode, useCallback, useRef, useState } from 'react'

import { useConfigurationLanguageServerInputInitializer } from '../uiModels/fplus/useConfigurationLanguageServerInputInitializer'
import { formatRequestKey } from './formatHelpers'
import { Provider } from './fPlusFormattedExpressionsContext'
import type { FormatRequest } from './types'

type ExpressionsMap = Map<number, Map<string, string>>

type Props = {
  children: ReactNode
}

const FPlusFormattedExpressionsProvider = ({ children }: Props) => {
  // Maintains a map of pending format requests, that still haven't been processed by the language services. Allows piling up
  // format requests between debounced formatter calls.
  const pendingFormatRequests = useRef<Map<number, Set<string>>>(new Map())
  // State based formatted expressions that allow child components to rerender when the context format function is called.
  const [formattedExpressions, setFormattedExpressions] =
    useState<ExpressionsMap>(new Map())
  // Non state based formatted expressions that stay in sync with the formattedExpressions state and allows format and
  // requestFormat functions exposed by the context to remain stable.
  const formattedExpressionsRef = useRef<ExpressionsMap>(formattedExpressions)
  const languageServerInputProvider =
    useConfigurationLanguageServerInputInitializer()

  // Debounced function that takes a batch of format requests and cals the language server format function. Updates the
  // format expressions state and the formattedExpressionsRef to increment the cached formatted expressions with the new set of expressions.
  const debouncedFormat = useDebounceCallback(
    async (requestedFormats: Map<number, Set<string>>) => {
      pendingFormatRequests.current = new Map()

      const allFormatCalls = Array.from(requestedFormats.entries()).map(
        async ([width, nonFormattedExpressions]) => {
          const expressions = Array.from(nonFormattedExpressions.values())
          const languageServerInput = languageServerInputProvider({
            expressions,
          })

          const formattedCode = await getFPlusLanguageServer().format(
            languageServerInput,
            width!,
          )

          return [
            width,
            new Map(
              expressions.map((expression, index) => [
                expression,
                formattedCode.content.at(index) ?? '',
              ]),
            ),
          ] as const
        },
      )

      const formatResults = await Promise.all(allFormatCalls)

      setFormattedExpressions((previousValue) => {
        const nextValue = new Map(previousValue)

        formatResults.forEach(([width, formattedExpressionsMap]) => {
          const nextWidthMap = nextValue.get(width) ?? new Map<string, string>()
          formattedExpressionsMap.forEach(
            (formattedExpression, expressionKey) => {
              nextWidthMap.set(expressionKey, formattedExpression)
            },
          )
          nextValue.set(width, nextWidthMap)
        })

        formattedExpressionsRef.current = nextValue
        return nextValue
      })
    },
    1000,
    [],
  )

  // Registers a format request and calls the debounced formatting service.
  const requestFormat = useCallback(
    (request: FormatRequest) => {
      const requestKey = formatRequestKey(request)

      if (!requestKey.length || !request.width) {
        return
      }

      if (
        !formattedExpressionsRef.current.get(request.width)?.get(requestKey)
      ) {
        if (!pendingFormatRequests.current.has(request.width)) {
          pendingFormatRequests.current.set(request.width, new Set<string>())
        }

        const widthSet = pendingFormatRequests.current.get(request.width)
        assertNotEmpty(widthSet)

        widthSet.add(requestKey)

        debouncedFormat(pendingFormatRequests.current)
      }
    },
    [debouncedFormat],
  )

  // Returns a formatted expression with the value that's found in local state,
  // if non exists then we take the value formatted to the closest width, and if there's none
  // the non formatted expression is returned as a fallback.
  const format = useCallback(
    (request: FormatRequest) => {
      const formatKey = formatRequestKey(request)
      let formattedCode = formattedExpressions
        .get(request.width)
        ?.get(formatKey)

      if (!formattedCode) {
        const formatSizesForKey = formattedExpressions
          .entries()
          .filter(([, formattedCode]) => formattedCode.has(formatKey))
          .map(([width]) => width)
          .toArray()

        if (formatSizesForKey.length) {
          const distancesFromCurrentWidth = formatSizesForKey.map((size) =>
            Math.abs(size - request.width),
          )
          const minimumDistance = Math.min(...distancesFromCurrentWidth)
          const closestWidth = formatSizesForKey.at(
            distancesFromCurrentWidth.indexOf(minimumDistance),
          )

          assertNotEmpty(closestWidth)

          formattedCode = formattedExpressions.get(closestWidth)?.get(formatKey)
        }
      }

      return formattedCode ?? request.expression
    },
    [formattedExpressions],
  )

  const formatDirectly = useCallback(
    async (expressions: string[], width: number) => {
      const formatted = await getFPlusLanguageServer().format(
        languageServerInputProvider({ expressions: [...expressions] }),
        width,
      )

      return expressions.map(
        (expression, index) => formatted.content.at(index) ?? expression,
      )
    },
    [languageServerInputProvider],
  )

  return (
    <Provider value={{ requestFormat, format, formatDirectly }}>
      {children}
    </Provider>
  )
}

export { FPlusFormattedExpressionsProvider }
